import type { AssistantRun } from '@charitystack/dashboard-assistant-contracts';
import type { DashboardAssistantDockMessage } from '../../../../contexts/DashboardAssistantDockContext';

export type DurableRunCompletion =
  | { state: 'waiting' }
  | { state: 'awaiting_final_entry' }
  | { state: 'awaiting_lock_release' }
  | { state: 'complete' }
  | { state: 'failed' }
  | { state: 'cancelled' };

export function hasFinalAssistantMessageForRun(
  messages: readonly DashboardAssistantDockMessage[],
  runId: string,
) {
  return messages.some(
    (message) =>
      message.role === 'assistant' &&
      message.kind === 'message' &&
      message.runId === runId &&
      message.content.trim().length > 0,
  );
}

/**
 * DynamoDB's run row owns lifecycle and its correlated message entry owns the
 * visible answer. Rich action cards are progress, never terminal completion.
 */
export function resolveDurableRunCompletion(
  run: AssistantRun,
  messages: readonly DashboardAssistantDockMessage[],
): DurableRunCompletion {
  if (
    (run.status === 'succeeded' || run.status === 'failed' || run.status === 'cancelled') &&
    run.threadReadyForNewRun !== true
  ) {
    return { state: 'awaiting_lock_release' };
  }
  if (run.status === 'failed') {
    return { state: 'failed' };
  }
  if (run.status === 'cancelled') {
    return { state: 'cancelled' };
  }
  if (run.status !== 'succeeded') {
    return { state: 'waiting' };
  }

  return hasFinalAssistantMessageForRun(messages, run.runId)
    ? { state: 'complete' }
    : { state: 'awaiting_final_entry' };
}
